import {
    Button,
    Flex,
    Modal,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Text,
} from '@salesforce/retail-react-app/app/components/shared/ui'
import React from 'react'
import {remCalc} from '../../../theme/_utils'
import {AlertIcon} from '../../../components/icons'
import PropTypes from 'prop-types'
import {FormattedMessage} from 'react-intl'
import Link from '@salesforce/retail-react-app/app/components/link'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'

const ProductRegistrationModal = ({
    isOpen,
    onClose,
    productName,
    onRegisterAnyway,
    showActions = true,
    isSerialNumberDuplicate = false,
    sameGigyaId = false,
    requiresSerialNumberReinput = false,
}) => {
    const navigate = useNavigation()
    if (!isOpen) return null

    const duplicateDestination = sameGigyaId ? '/account/registered-products' : '/'

    return (
        <Modal
            isOpen={isOpen}
            onClose={isSerialNumberDuplicate ? () => navigate(duplicateDestination) : onClose}
            isCentered
            blockScrollOnMount={false}
        >
            <ModalOverlay />
            <ModalContent width={{lg: remCalc(451)}} minWidth={remCalc(343)}>
                <ModalHeader
                    paddingX={remCalc(26)}
                    paddingTop={remCalc(26)}
                    paddingBottom={remCalc(24)}
                >
                    <Flex justifyContent="space-between">
                        <Flex gap={remCalc(17)} alignItems="center">
                            {!requiresSerialNumberReinput && (
                                <AlertIcon boxSize="iconsSize.medium" />
                            )}
                            <Text fontWeight="400" textStyle="body-small" color="text.default">
                                {isSerialNumberDuplicate && !sameGigyaId ? (
                                    <FormattedMessage
                                        id="product_registration_modal.registration_cannot_be_completed_title"
                                        defaultMessage="Registration cannot be completed"
                                    />
                                ) : requiresSerialNumberReinput ? null : (
                                    <FormattedMessage
                                        id="product_registration_modal.already_registered_title"
                                        defaultMessage="Product already registered"
                                    />
                                )}
                            </Text>
                        </Flex>
                    </Flex>
                </ModalHeader>
                <ModalBody data-testid="modal-body" paddingX={remCalc(26)} paddingY={0}>
                    <Text
                        fontWeight="400"
                        textStyle="body-small"
                        color="text.default"
                        whiteSpace={
                            isSerialNumberDuplicate && !sameGigyaId ? 'pre-line' : undefined
                        }
                    >
                        {requiresSerialNumberReinput ? (
                            <>
                                <FormattedMessage
                                    id="product_registration_modal.reinput_serial_number"
                                    defaultMessage="We had some issues validating your serial number. Please re-input the code again by typing it or scanning the dedicated label on the product."
                                />
                                <br />
                                <Button
                                    onClick={onClose}
                                    px={0}
                                    fontWeight="400"
                                    textStyle="body-small"
                                    textColor="button.text.text-default"
                                    textDecoration="underline"
                                    backgroundColor="transparent"
                                    _hover={{backgroundColor: 'transparent'}}
                                    _active={{backgroundColor: 'transparent'}}
                                >
                                    <FormattedMessage
                                        id="product_registration_modal.got_it"
                                        defaultMessage="Got it"
                                    />
                                </Button>
                            </>
                        ) : isSerialNumberDuplicate ? (
                            sameGigyaId ? (
                                <FormattedMessage
                                    id="product_registration_modal.duplicate_serial_number_same_owner"
                                    defaultMessage={
                                        'There is already a registration associated to this serial number and your email address. Please visit your "Registered Products" section within the MyAccount to review your registration details. If you do not have an account, you will need to create one. <link>Login or Create an Account</link>'
                                    }
                                    values={{
                                        link: (chunks) => (
                                            <Link
                                                to="/account/registered-products"
                                                color="button.text.text-default"
                                                textDecoration="underline"
                                            >
                                                {chunks}
                                            </Link>
                                        ),
                                    }}
                                />
                            ) : (
                                <FormattedMessage
                                    id="product_registration_modal.duplicate_serial_number_other_owner"
                                    defaultMessage={
                                        'This product is already registered with another customer. To resolve this:<br></br>1) Try again: double-check your Serial Number and upload a clear photo of your receipt<br></br>2) Received as gift? Ask the person who gave it to you to remove the registration from their account first<br></br>3) Still stuck? Contact Customer Care for assistance<br></br><link>Back to Home Page</link>'
                                    }
                                    values={{
                                        br: () => <br />,
                                        link: (chunks) => (
                                            <Link
                                                to="/"
                                                color="button.text.text-default"
                                                textDecoration="underline"
                                            >
                                                {chunks}
                                            </Link>
                                        ),
                                    }}
                                />
                            )
                        ) : (
                            <FormattedMessage
                                id="product_registration_modal.already_registered"
                                defaultMessage="There is already a registration for {productName} associated with this email address. If you inserted the wrong details, please edit or delete your old product registration before registering it again. If you want to register a different product, please start again."
                                values={{
                                    productName,
                                }}
                            />
                        )}
                    </Text>
                </ModalBody>
                <ModalFooter
                    paddingX={remCalc(26)}
                    paddingTop={remCalc(26)}
                    paddingBottom={remCalc(24)}
                >
                    {showActions && (
                        <Flex flexDir={'column'} gap={remCalc(24)} w={'100%'}>
                            <Button
                                as={Link}
                                variant="primary"
                                onClick={onClose}
                                width="100%"
                                to="/account/registered-products"
                            >
                                <FormattedMessage
                                    id="product_registration_modal.registration_details"
                                    defaultMessage="Review and edit my registered products"
                                />
                            </Button>
                            <Button
                                onClick={onRegisterAnyway}
                                variant="link"
                                textDecoration={'underline'}
                                color="button.text.text-default"
                                width="100%"
                            >
                                <FormattedMessage
                                    defaultMessage="I purchased the same appliance more than once"
                                    id="product_registration_modal.register_another_product"
                                />
                            </Button>
                        </Flex>
                    )}
                </ModalFooter>
            </ModalContent>
        </Modal>
    )
}

ProductRegistrationModal.propTypes = {
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
    productName: PropTypes.string,
    onRegisterAnyway: PropTypes.func,
    showActions: PropTypes.bool,
    isSerialNumberDuplicate: PropTypes.bool,
    sameGigyaId: PropTypes.bool,
    requiresSerialNumberReinput: PropTypes.bool,
}

export default ProductRegistrationModal
